import React, { useState, useEffect } from 'react';

const Table = ({ columns, data, onEdit, onDelete, onView, emptyMessage = 'Aucune donnée' }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!data || data.length === 0) {
    return (
      <div style={styles.empty}>
        <p>{emptyMessage}</p>
      </div>
    );
  }

  // Mobile card view
  if (isMobile) {
    return (
      <div style={styles.cardsContainer}>
        {data.map((row, rowIndex) => (
          <div key={rowIndex} style={styles.card}>
            {columns.map((col, colIndex) => (
              <div 
                key={colIndex} 
                style={{
                  ...styles.cardRow,
                  ...(colIndex === columns.length - 1 ? styles.cardRowLast : {})
                }}
              >
                <div style={styles.cardLabel}>{col.header}</div>
                <div style={styles.cardValue}>
                  {col.render ? col.render(row) : row[col.accessor]}
                </div>
              </div>
            ))}
            {(onEdit || onDelete || onView) && (
              <div style={styles.cardActions}>
                {onView && (
                  <button
                    onClick={() => onView(row)}
                    style={styles.cardActionButton}
                    title="Voir"
                  >
                    👁️ Voir
                  </button>
                )}
                {onEdit && (
                  <button
                    onClick={() => onEdit(row)}
                    style={styles.cardActionButton}
                    title="Modifier"
                  >
                    ✏️ Modifier
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => onDelete(row)}
                    style={{ ...styles.cardActionButton, ...styles.deleteButton }}
                    title="Supprimer"
                  >
                    🗑️ Supprimer
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Desktop table view
  return (
    <div style={styles.tableContainer}>
      <div style={styles.tableWrapper}>
        <table style={styles.table}>
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th key={index} style={styles.th}>
                  {col.header}
                </th>
              ))}
            {(onEdit || onDelete || onView) && (
              <th style={styles.th}>Actions</th>
            )}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex} style={styles.tr}>
                {columns.map((col, colIndex) => (
                  <td key={colIndex} style={styles.td}>
                    {col.render ? col.render(row) : row[col.accessor]}
                  </td>
                ))}
              {(onEdit || onDelete || onView) && (
                <td style={styles.td}>
                  <div style={styles.actions}>
                    {onView && (
                      <button
                        onClick={() => onView(row)}
                        style={styles.actionButton}
                        title="Voir"
                      >
                        👁️
                      </button>
                    )}
                    {onEdit && (
                      <button
                        onClick={() => onEdit(row)}
                        style={styles.actionButton}
                        title="Modifier"
                      >
                        ✏️
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => onDelete(row)}
                        style={{ ...styles.actionButton, color: 'var(--danger)' }}
                        title="Supprimer"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </td>
              )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  tableContainer: {
    background: 'white',
    borderRadius: '0.75rem',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '600px',
  },
  th: {
    padding: '1rem',
    textAlign: 'left',
    backgroundColor: 'var(--light)',
    fontWeight: 600,
    fontSize: '0.875rem',
    color: 'var(--text)',
    borderBottom: '2px solid var(--border)',
  },
  tr: {
    borderBottom: '1px solid var(--border)',
    transition: 'background-color 0.2s ease',
  },
  td: {
    padding: '1rem',
    fontSize: '0.875rem',
    color: 'var(--text)',
  },
  actions: {
    display: 'flex',
    gap: '0.5rem',
  },
  actionButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1.25rem',
    padding: '0.25rem',
    transition: 'transform 0.2s ease',
  },
  empty: {
    padding: '3rem',
    textAlign: 'center',
    color: 'var(--text-light)',
    background: 'white',
    borderRadius: '0.75rem',
  },
  // Mobile card styles
  cardsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  card: {
    background: 'white',
    borderRadius: '0.75rem',
    padding: '1.5rem',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    border: '1px solid var(--border)',
  },
  cardRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid var(--border)',
  },
  cardRowLast: {
    borderBottom: 'none',
    marginBottom: '0',
    paddingBottom: '0',
  },
  cardLabel: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--text-light)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  cardValue: {
    fontSize: '0.875rem',
    color: 'var(--text)',
    fontWeight: 500,
  },
  cardActions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '1px solid var(--border)',
  },
  cardActionButton: {
    flex: 1,
    padding: '0.75rem',
    borderRadius: '0.5rem',
    border: '1px solid var(--border)',
    background: 'white',
    color: 'var(--text)',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
  },
  deleteButton: {
    borderColor: 'var(--danger)',
    color: 'var(--danger)',
  },
  cardActionButtonHover: {
    background: 'var(--light)',
    transform: 'translateY(-1px)',
  },
  deleteButtonHover: {
    background: 'var(--danger)',
    color: 'white',
  },
};

export default Table;





